/* eslint-disable @typescript-eslint/naming-convention */
import {
   Component,
   ElementRef,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CallState, LoadingState } from '@lcs/shared/api';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { GlobalSearchListItem, GlobalSearchResultSet, NavigationDirection } from '../models/global-search.model';
import { GlobalSearchService } from '../services/global-search.service';
import { globalSearchSearchResultCleared } from '../state/global-search.actions';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-global-search-view',
    styleUrls: ['global-search-view.component.scss'],
    templateUrl: 'global-search-view.component.html',
    standalone: false
})
export class GlobalSearchViewComponent implements OnInit, OnDestroy {
   @Input() accountSearchCallState: CallState;

   @Input() accountSearchResults: GlobalSearchListItem[] | null;

   @Input() deviceSearchCallState: CallState;

   @Input() deviceSearchResults: GlobalSearchListItem[] | null;

   @Input() phoneNumberSearchCallState: CallState;

   @Input() phoneNumberSearchResults: GlobalSearchListItem[] | null;

   @Input() softphoneUsernameSearchCallState: CallState;

   @Input() softphoneUsernameSearchResults: GlobalSearchListItem[] | null;

   @Input() menuItemSearchResults: GlobalSearchListItem[] | null;

   @Output() completed = new EventEmitter();

   @Output() clickedInside = new EventEmitter<Event>();

   @ViewChild('panel') panel: ElementRef;

   @ViewChild('wrapper') wrapper: ElementRef;

   LoadingState = LoadingState;

   private destroy$ = new Subject<void>();

   constructor(
      public globalSearchServices: GlobalSearchService,
      private router: Router,
      private store: Store<AppState>
   ) {
      router.events
         .pipe(
            filter((event) => {
               return event instanceof NavigationEnd;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe(() => {
            this.close();
         });
   }

   handleClick(event: Event): void {
      this.clickedInside.emit(event);
   }

   handleKeyboardEvent(event: KeyboardEvent): void {
      switch (event.key) {
         case 'ArrowDown':
            this.focusNextItem(NavigationDirection.Forward);
            event.preventDefault();
            break;
         case 'ArrowUp':
            this.focusNextItem(NavigationDirection.Backward);
            event.preventDefault();
            break;
      }
   }

   ngOnDestroy(): void {
      document.removeEventListener('keydown', this.handleKeyboardEvent.bind(this));
      this.destroy$.next();
   }

   ngOnInit(): void {
      document.addEventListener('keydown', this.handleKeyboardEvent.bind(this));
   }

   hasDynamicData(): boolean {
      return this.globalSearchServices.hasDynamicData(this.getResultSet());
   }

   isLoading(): boolean {
      return this.globalSearchServices.isLoading(this.getResultSet());
   }

   onItemSelected(): void {
      this.completed.emit();
   }

   public focus(): void {
      this.wrapper.nativeElement.focus();
   }

   private anchorTagName = 'a';

   private focusNextItem(direction: NavigationDirection): void {
      const allAnchors: HTMLAnchorElement[] = this.getAllAnchors();
      const currentItem: HTMLAnchorElement | Element | null = document.activeElement;
      if (currentItem) {
         const currentItemIndex: number = allAnchors.indexOf(currentItem as HTMLAnchorElement);
         let targetItem: HTMLAnchorElement | null = null;

         switch (direction) {
            case NavigationDirection.Backward:
               targetItem = allAnchors[currentItemIndex - 1];
               break;
            case NavigationDirection.Forward:
               targetItem = allAnchors[currentItemIndex + 1];
               break;
         }

         if (targetItem) {
            this.setItemFocus(targetItem);
         }
      }
   }

   private getAllAnchors(): HTMLAnchorElement[] {
      return Array.from(this.panel.nativeElement.querySelectorAll(this.anchorTagName));
   }

   private close(): void {
      this.store.dispatch(globalSearchSearchResultCleared());
   }

   private getResultSet(): GlobalSearchResultSet {
      const resultSet = {
         callStates: {
            accounts: this.accountSearchCallState,
            devices: this.deviceSearchCallState,
            phoneNumbers: this.phoneNumberSearchCallState,
            softphoneUsernames: this.softphoneUsernameSearchCallState,
         },
         data: {
            accounts: this.accountSearchResults,
            devices: this.deviceSearchResults,
            phoneNumbers: this.phoneNumberSearchResults,
            softphoneUsernames: this.softphoneUsernameSearchResults,
         },
      } as GlobalSearchResultSet;

      return resultSet;
   }

   private setItemFocus(item: HTMLAnchorElement): void {
      if (this.panel !== null) {
         this.panel.nativeElement.querySelectorAll(this.anchorTagName).forEach((a: HTMLAnchorElement) => {
            return (a.tabIndex = -1);
         });
         item.tabIndex = 0;
         item.focus();
      }
   }
}
